import { FC, Suspense } from 'react';

import { IEnvironment } from 'relay-runtime';
import { Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { UsersIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';

import {
  Button,
  createLinkState,
  EdLink,
  PageHeading,
  TabPills,
} from 'shared-ui';

import { RoutingContext } from '../../../routing';
import { ErrorBoundaryFallback, OutletRoute } from '../../../components';
import { createIdentityUserRoutes } from './IdentityUserRoute';
import { IdentityUserCreate } from './IdentityUserCreate';
import { IdentityUserListContainer } from './IdentityUserListContainer';


export const IdentityLayout: FC = () => {
  const location = useLocation();
  const tabs = [createLinkState(location, 'Users', `users`, UsersIcon)];

  return (
    <Suspense fallback={'Loading Identity...'}>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <div className='flex flex-col'>
          <div className='flex items-center justify-between border-b bg-white px-4'>
            <div className='flex items-center gap-3'>
              <EdLink href='../../' name={'List Identity Users'}>
                <Button color='primary'>Back</Button>
              </EdLink>
              <TabPills tabs={tabs} />
            </div>
            <div className='flex items-center gap-2 p-4'>
              <PageHeading>Myn Identities</PageHeading>
              <div>
                <EdLink href='../create' name={'Create Identity'}>
                  <Button color='primary'>
                    Create Identity &nbsp;
                    <PlusIcon className='h-5 w-5' />
                  </Button>
                </EdLink>
              </div>
            </div>
          </div>
          <div>
            <Outlet />
          </div>
        </div>
      </ErrorBoundary>
    </Suspense>
  );
};

export const IdentityUsersListRoute: FC = () => {
  return (
    <Suspense fallback={'Loading Identity Users...'}>
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <IdentityUserListContainer />
      </ErrorBoundary>
    </Suspense>
  );
};

export const createIdentityRoutes = (
  routePrefix: string,
  environment: IEnvironment,
  context: RoutingContext,
): Array<RouteObject> => {
  const routeId = `${routePrefix}-identity-users`;
  const listRouteId = `${routeId}-list`;

  return [
    {
      id: routeId,
      path: '*',
      element: <IdentityLayout />,
      children: [
        {
          id: listRouteId,
          path: 'users/*',
          element: <IdentityUsersListRoute />,
          children: [
            {
              id: `${listRouteId}-identity-users`,
              path: ':identityUserCode/*',
              element: <OutletRoute />,
              children: createIdentityUserRoutes(
                `${listRouteId}-identity-user`,
                environment,
                context,
                true,
                true,
              ),
            },
          ],
        },
        {
          id: `${routeId}-identity-user`,
          path: ':identityUserCode/*',
          element: <OutletRoute />,
          children: createIdentityUserRoutes(
            `${routePrefix}-identity-user`,
            environment,
            context,
            false,
            false,
          ),
        },
        {
          id: `${routeId}-create`,
          path: 'create/*',
          element: <IdentityUserCreate />,
        },
        {
          path: '*',
          element: <Navigate to={'users'} replace />,
        },
      ],
    },
  ];
};
